/**
 * A person attached to an enrollment (Enrollment.EnrollmentMembers) — a chirp room learner, or an
 * instructor covering lessons as a substitute. A chirp room is anchored to a placeholder learner
 * account, so members there are never the enrollment's learnerUserID.
 *
 * Membership data only — resolve names/images through UserStateService (cached User.Api summaries).
 * Viewers who may see rosters (admin, the room's instructor, its institution's ambassadors) have the
 * member IDs folded into their identity scope by the enrollment relationship graph; members themselves
 * do not, so co-member identities never resolve for them.
 */
export interface IEnrollmentMember {
	enrollmentMemberID: number;
	enrollmentID: number;
	userID: number;

	/** Enrollment.EnrollmentMemberRoles — 3 = Learner, 6 = Substitute. */
	roleID: number;

	startDate: string;

	/** Null while the person is still a member. */
	endDate: string | null;

	/**
	 * The room's timezone (Core.TimeZones), which `startDate` is anchored to (midnight there) and the
	 * one both dates must be rendered in — via the slot pattern, NOT `tzDate` — so every viewer reads
	 * the same calendar dates. Null only if the room's placeholder account lost its timezone; fall
	 * back to the viewer's selected timezone.
	 */
	timezoneId: number | null;

	/** Server-computed convenience flag: true when `endDate` is unset. */
	isActive: boolean;

	createDate: string;
	createdBy: number;
	lastModifiedDate: string | null;
	lastModifiedBy: number | null;
}

export interface IAddEnrollmentMemberRequest {
	userID: number;

	/**
	 * Omit to let the server default to Learner. Only the seeded roles are storable — anything else is
	 * rejected, because those relationships live on their own tables. Substitutes go through
	 * `assignSubstitute` rather than this route, which also mints their history row and moves lessons.
	 */
	roleID?: EnrollmentMemberRole;

	/**
	 * A calendar date, `'YYYY-MM-DD'` — no time, no timezone. The server anchors it to midnight in
	 * the room's timezone, so do not convert it to UTC here; sending an instant is what used to shift
	 * joined dates by a day between viewers. Omit for "joining today" (server computes today in room
	 * time). When backfilling an existing room, set this to the room's start date or the member will
	 * not see the sessions they have already attended.
	 */
	startDate?: string;

	/** Set only when the membership's end is known up front. Null means open-ended. */
	endDate?: string;
}

/** Puts a substitute instructor on specific lessons of an enrollment they do not own. */
export interface IAssignSubstituteRequest {
	instructorUserID: number;

	/** The lessons they are covering. At least one is required. */
	lessonIDs: number[];

	/** Defaults to now. Set to the earliest covered lesson's date when backfilling past coverage. */
	startDate?: string;

	/** Set only for a fixed stint; omit for open-ended coverage ended through `endSubstitute`. */
	endDate?: string;

	/** Why the substitute is needed, for the audit trail. Optional. */
	reason?: string;
}

/**
 * The outcome of an assignment. `lessonsRepointed` can legitimately be below the number requested —
 * on a repeat assignment the lessons are already on the substitute's history row, so nothing moves.
 */
export interface ISubstituteAssignment {
	enrollmentMemberID: number;
	enrollmentHistoryID: number;
	lessonsRepointed: number;
	reusedExistingMembership: boolean;

	/**
	 * True when the request named the enrollment's own instructor and was handled as the undo of a
	 * substitution: the lessons went back onto the current history row, `enrollmentMemberID` is 0, and
	 * nothing was created. Read it before reporting the outcome — otherwise an undo reads back to the
	 * admin as the substitution they just reversed.
	 */
	returnedToPrimaryInstructor: boolean;
}

/**
 * Mirrors Enrollment.DTO.Enums.EnrollmentMemberRole. Deliberately a superset of what can be stored:
 * only `Learner` and `Substitute` are seeded in the lookup, and the other values describe *why*
 * someone can see an enrollment rather than a row on its roster.
 */
export enum EnrollmentMemberRole {
	/** On Enrollment.Enrollments.LearnerUserID. Never a member row. */
	PrimaryLearner = 1,
	/** The PRIMARY instructor, from Enrollment.EnrollmentHistory where isCurrent. Never a member row. */
	Instructor = 2,
	/** A learner on the enrollment — today, a chirp room member. Storable. */
	Learner = 3,
	/** From Core.UserAssociations. Never a member row. */
	Parent = 4,
	/** From Core.InstitutionAssociations. Never a member row. */
	InstitutionUser = 5,
	/** An instructor covering specific lessons on someone else's enrollment. Storable. */
	Substitute = 6
}
